<template>
  <div class="newsletter-modal critical-component-hide">
    <newsletter-form
      :title="title"
      :subtitle="subtitle"
      :disclaimer="disclaimer"
      :title-id="liquid && `section-title-${liquid.sectionId}`"
      block-class="newsletter-modal"
      @state="setFormState"
    />

    <btn
      v-if="button"
      v-show="formState === 'SUCCESS'"
      block-class="newsletter-modal"
      :label="button"
      modifiers="block"
      @click="closeOverlay(overlay)"
    />
  </div>
</template>

<script>

/**
 * Vue: Newsletter modal (newsletter-modal)
 * -----------------------------------------------------------------------------
 * An automatic, delayed modal for prompting newsletter subscriptions.
 *
 * @param {String} title - The form title.
 * @param {String} subtitle - The form subtitle.
 * @param {String} disclaimer - The form disclaimer.
 * @param {String} button - The button label.
 * @param {String} overlay - The overlay namespace, if used.
 * @param {Object} liquid - Liquid data including strings, arrays and captured content.
 * @param {String} liquid.sectionId - The section identifier.
 *
 */
import { mapActions, mapState } from 'vuex'

import Btn from '~global/btn/btn'
import NewsletterForm from '~async/newsletter-form/newsletter-form'

export default {
  name: 'NewsletterModal',

  components: {
    Btn,
    NewsletterForm,
  },

  props: {
    title: {
      type: String,
      default: '',
    },
    subtitle: {
      type: String,
      default: '',
    },
    disclaimer: {
      type: String,
      default: '',
    },
    button: {
      type: String,
      default: '',
    },
    overlay: {
      type: String,
      default: '',
    },
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
      }),
    },
  },

  data() {
    return {
      formState: 'IDLE',
      scrollDistance: null,
      scroll: {
        start: null,
        timeout: null,
        end: null,
      },
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
      screenHeight: (state) => state.index.screen.height,
    }),
  },

  watch: {

    /**
     * Resets the event listeners when the device type changes.
     */
    isMobile() {
      if (!this.overlay) {
        return
      }

      this.setEventListeners()
    },

    /**
     * If the scroll up distance is twice the viewport, show the modal.
     */
    scrollDistance() {
      const isUp = this.scrollDistance < 0
      const isThreshold = Math.abs(this.scrollDistance) > this.screenHeight * 2

      if (isUp && isThreshold) {
        this.openOverlay({ namespace: this.overlay })
      }
    },
  },

  mounted() {
    if (!this.overlay) {
      return
    }

    this.setEventListeners()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
      openOverlay: 'overlays/open',
    }),

    /**
     * Sets the event listeners.
     */
    setEventListeners() {
      this.setScrollEvents(!this.isMobile)
      this.setMouseEvents(this.isMobile)
    },

    /**
     * Sets the local form state.
     * @param {String} state - The incoming form state.
     */
    setFormState(state) {
      if (!state) {
        return
      }

      this.formState = state
    },

    /**
     * Sets the mouse event listeners.
     * @param {Boolean} off - Removes the event listeners.
     */
    setMouseEvents(off = false) {
      document[
        off ? 'removeEventListener' : 'addEventListener'
      ](
        'mouseout',
        this.handleMouseout,
      )
    },

    /**
     * Sets the scroll event listeners.
     * @param {Boolean} off - Removes the event listeners.
     */
    setScrollEvents(off = false) {
      document[
        off ? 'removeEventListener' : 'addEventListener'
      ](
        'scroll',
        this.detectScrollDistance,
      )
    },

    /**
     * Handles the mouse out event.
     * @param {Object} event - The event payload.
     */
    handleMouseout(event) {
      if (!event.toElement && !event.relatedTarget) {
        this.openOverlay({ namespace: this.overlay })
      }
    },

    /**
     * Detects the scroll distance when the method runs.
     * @see https://gomakethings.com/detecting-scroll-distances-with-vanilla-js/#detecting-scrolling-distances
     */
    detectScrollDistance() {
      if (!this.scroll.start) {
        this.scroll.start = window.pageYOffset
      }

      window.clearTimeout(this.scroll.timeout)

      this.scroll.timeout = setTimeout(() => {
        this.scroll.end = window.pageYOffset
        this.scrollDistance = this.scroll.end - this.scroll.start

        this.scroll.start = null
        this.scroll.end = null
      }, 66)
    },
  },
}
</script>

<style lang="scss">
@import './newsletter-modal';
@import './newsletter-modal-overlay';
</style>
